import { createContext, useContext, useMemo, useEffect, useRef } from "react"
import { useAppSelector, useAppDispatch } from "hooks/redux"
import { useDashboardId } from "./dashboard-id"
import { tradeSlice, DEFAULT_TRADE_STATE } from "../store/trade-slice"

export const TradeContext = createContext()

export default function useTradeContext(parentContext) {
  const parentContextValue = useContext(parentContext)
  const { instrument, modelId } = parentContextValue

  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  const state = useAppSelector((s) => s.dashboard.trade[dashboardId] ?? DEFAULT_TRADE_STATE)

  const prevInstrumentIdRef = useRef()
  const prevModelIdRef = useRef()

  useEffect(() => {
    const instrumentChanged = instrument?.id !== prevInstrumentIdRef.current
    const modelChanged = modelId !== prevModelIdRef.current

    if (instrumentChanged || modelChanged) {
      dispatch(tradeSlice.actions.resetTradeContext({ dashboardId }))
    }

    prevInstrumentIdRef.current = instrument?.id
    prevModelIdRef.current = modelId
  }, [instrument?.id, modelId, dispatch, dashboardId])

  const contextUpdaters = useMemo(() => ({
    addTrade: (trade) => dispatch(tradeSlice.actions.addTrade({ dashboardId, trade })),
    resetTrades: () => dispatch(tradeSlice.actions.resetTrades({ dashboardId })),
    setTradedPosition: (tradedPosition, tradedPositionField) => dispatch(tradeSlice.actions.setTradedPosition({ dashboardId, tradedPosition, tradedPositionField })),
    setExpanded: (isExpanded) => dispatch(tradeSlice.actions.setExpanded({ dashboardId, isExpanded })),
    updateTradeFilters: (filter) => dispatch(tradeSlice.actions.updateTradeFilters({ dashboardId, filter })),
    setAssetViewGroupBy: (assetViewGroupBy) => dispatch(tradeSlice.actions.setAssetViewGroupBy({ dashboardId, assetViewGroupBy })),
  }), [dispatch, dashboardId])

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...state,
    ...contextUpdaters,
  }), [contextUpdaters, state, parentContextValue])

  return [TradeContext, contextValue]
}
